import { useState, useMemo } from 'react';
import { Table, Badge, Text, Group, Skeleton, Box, UnstyledButton } from '@mantine/core';
import { IconChevronUp, IconChevronDown, IconSelector } from '@tabler/icons-react';
import type { Shipment, RiskLevel, CustomerTier, ShipmentStatus } from '../types';

type ViewMode = 'inflight' | 'exceptions' | 'complete';

interface ShipmentTableProps {
  shipments: Shipment[];
  loading: boolean;
  onSelect: (shipment: Shipment) => void;
  selectedId?: string;
  filter?: string;
  viewMode?: ViewMode;
}

// Province/State abbreviations
const REGION_ABBREV: Record<string, string> = {
  // Canadian Provinces
  'Ontario': 'ON',
  'Quebec': 'QC',
  'British Columbia': 'BC',
  'Alberta': 'AB',
  'Manitoba': 'MB',
  'Saskatchewan': 'SK',
  'Nova Scotia': 'NS',
  'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL',
  'Prince Edward Island': 'PE',
  'Northwest Territories': 'NT',
  'Yukon': 'YT',
  'Nunavut': 'NU',
  // US States
  'Pennsylvania': 'PA',
  'New York': 'NY',
  'New Jersey': 'NJ',
  'Massachusetts': 'MA',
  'Connecticut': 'CT',
  'Maryland': 'MD',
  'Virginia': 'VA',
  'Ohio': 'OH',
  'Michigan': 'MI',
  'Florida': 'FL',
  'California': 'CA',
  'Texas': 'TX',
  'Illinois': 'IL',
  'Georgia': 'GA',
  'North Carolina': 'NC',
  'South Carolina': 'SC',
  'Washington': 'WA',
  'Oregon': 'OR',
  'Arizona': 'AZ',
  'Colorado': 'CO',
  'Tennessee': 'TN',
  'Indiana': 'IN',
  'Wisconsin': 'WI',
  'Minnesota': 'MN',
  'Missouri': 'MO',
  'Kentucky': 'KY',
  'Louisiana': 'LA',
  'Alabama': 'AL',
  'Oklahoma': 'OK',
  'Iowa': 'IA',
  'Nevada': 'NV',
  'Utah': 'UT',
  'Kansas': 'KS',
  'Arkansas': 'AR',
  'Mississippi': 'MS',
  'New Mexico': 'NM',
  'Nebraska': 'NE',
  'West Virginia': 'WV',
  'Delaware': 'DE',
  'Maine': 'ME',
  'New Hampshire': 'NH',
  'Rhode Island': 'RI',
  'Vermont': 'VT',
  'Hawaii': 'HI',
  'Alaska': 'AK',
  'Montana': 'MT',
  'Idaho': 'ID',
  'Wyoming': 'WY',
  'North Dakota': 'ND',
  'South Dakota': 'SD',
};

// City-level coordinates for common destinations
const CITY_COORDS: Record<string, { lat: number; lon: number }> = {
  // Ontario cities
  'toronto': { lat: 43.65, lon: -79.38 },
  'ottawa': { lat: 45.42, lon: -75.69 },
  'mississauga': { lat: 43.59, lon: -79.64 },
  'brampton': { lat: 43.73, lon: -79.76 },
  'hamilton': { lat: 43.26, lon: -79.87 },
  'london': { lat: 42.98, lon: -81.25 },
  'markham': { lat: 43.86, lon: -79.34 },
  'vaughan': { lat: 43.84, lon: -79.51 },
  'kitchener': { lat: 43.45, lon: -80.49 },
  'windsor': { lat: 42.31, lon: -83.04 },
  'richmond hill': { lat: 43.87, lon: -79.44 },
  'oakville': { lat: 43.45, lon: -79.68 },
  'burlington': { lat: 43.33, lon: -79.79 },
  'greater sudbury': { lat: 46.49, lon: -81.01 },
  'sudbury': { lat: 46.49, lon: -81.01 },
  'barrie': { lat: 44.39, lon: -79.69 },
  'oshawa': { lat: 43.90, lon: -78.86 },
  'st. catharines': { lat: 43.16, lon: -79.24 },
  'cambridge': { lat: 43.36, lon: -80.31 },
  'kingston': { lat: 44.23, lon: -76.48 },
  'guelph': { lat: 43.55, lon: -80.25 },
  'thunder bay': { lat: 48.38, lon: -89.25 },
  'waterloo': { lat: 43.46, lon: -80.52 },
  'brantford': { lat: 43.14, lon: -80.26 },
  'pickering': { lat: 43.84, lon: -79.09 },
  'niagara falls': { lat: 43.09, lon: -79.08 },
  'peterborough': { lat: 44.30, lon: -78.32 },
  'sault ste. marie': { lat: 46.52, lon: -84.35 },
  'newmarket': { lat: 44.06, lon: -79.46 },
  'ajax': { lat: 43.85, lon: -79.03 },
  'whitby': { lat: 43.90, lon: -78.94 },
  'milton': { lat: 43.52, lon: -79.88 },
  'etobicoke': { lat: 43.65, lon: -79.55 },
  'scarborough': { lat: 43.77, lon: -79.26 },
  'north york': { lat: 43.77, lon: -79.41 },
  'nepean': { lat: 45.35, lon: -75.73 },
  'concord': { lat: 43.81, lon: -79.49 },
  'thornhill': { lat: 43.81, lon: -79.42 },
  'aurora': { lat: 44.01, lon: -79.47 },
  'stouffville': { lat: 43.97, lon: -79.25 },
  'orangeville': { lat: 43.92, lon: -80.10 },
  'collingwood': { lat: 44.50, lon: -80.22 },
  'orillia': { lat: 44.61, lon: -79.42 },
  'belleville': { lat: 44.16, lon: -77.38 },
  'sarnia': { lat: 42.97, lon: -82.40 },
  'chatham': { lat: 42.40, lon: -82.18 },
  'stratford': { lat: 43.37, lon: -80.98 },
  'woodstock': { lat: 43.13, lon: -80.75 },
  'st. thomas': { lat: 42.77, lon: -81.18 },
  'st thomas': { lat: 42.77, lon: -81.18 },
  'leamington': { lat: 42.05, lon: -82.60 },
  'timmins': { lat: 48.48, lon: -81.33 },
  'north bay': { lat: 46.31, lon: -79.46 },
  'cornwall': { lat: 45.02, lon: -74.73 },
  'welland': { lat: 42.99, lon: -79.25 },
  'port colborne': { lat: 42.89, lon: -79.25 },
  'fort erie': { lat: 42.91, lon: -78.93 },
  'grimsby': { lat: 43.20, lon: -79.56 },
  'stoney creek': { lat: 43.22, lon: -79.77 },
  'ancaster': { lat: 43.22, lon: -79.99 },
  'dundas': { lat: 43.27, lon: -79.96 },
  'georgetown': { lat: 43.65, lon: -79.92 },
  'halton hills': { lat: 43.63, lon: -79.95 },
  'caledon': { lat: 43.87, lon: -79.87 },
  'bolton': { lat: 43.88, lon: -79.73 },
  'innisfil': { lat: 44.30, lon: -79.58 },
  'wasaga beach': { lat: 44.52, lon: -80.02 },
  'midland': { lat: 44.75, lon: -79.88 },
  'gravenhurst': { lat: 44.92, lon: -79.37 },
  'bracebridge': { lat: 45.03, lon: -79.31 },
  'huntsville': { lat: 45.33, lon: -79.22 },
  'parry sound': { lat: 45.34, lon: -80.03 },
  'cobourg': { lat: 43.96, lon: -78.17 },
  'port hope': { lat: 43.95, lon: -78.29 },
  'bowmanville': { lat: 43.91, lon: -78.69 },
  'clarington': { lat: 43.94, lon: -78.61 },
  'uxbridge': { lat: 44.11, lon: -79.12 },
  'lindsay': { lat: 44.35, lon: -78.74 },
  'kawartha lakes': { lat: 44.35, lon: -78.74 },
  'quinte west': { lat: 44.18, lon: -77.57 },
  'napanee': { lat: 44.25, lon: -76.95 },
  'brockville': { lat: 44.59, lon: -75.68 },
  'pembroke': { lat: 45.83, lon: -77.11 },
  'petawawa': { lat: 45.90, lon: -77.28 },
  'kanata': { lat: 45.30, lon: -75.90 },
  'orleans': { lat: 45.47, lon: -75.52 },
  'gloucester': { lat: 45.40, lon: -75.55 },
  'fergus': { lat: 43.71, lon: -80.38 },
  'elora': { lat: 43.68, lon: -80.43 },
  'new tecumseth': { lat: 44.08, lon: -79.75 },
  'alliston': { lat: 44.15, lon: -79.87 },
  'east york': { lat: 43.69, lon: -79.33 },
  'west york': { lat: 43.68, lon: -79.47 },
  'york': { lat: 43.70, lon: -79.42 },
  'breslau': { lat: 43.47, lon: -80.40 },

  // Quebec cities
  'montreal': { lat: 45.50, lon: -73.57 },
  'quebec city': { lat: 46.81, lon: -71.21 },
  'laval': { lat: 45.57, lon: -73.69 },
  'gatineau': { lat: 45.48, lon: -75.70 },
  'longueuil': { lat: 45.54, lon: -73.51 },
  'sherbrooke': { lat: 45.40, lon: -71.89 },
  'saguenay': { lat: 48.43, lon: -71.07 },
  'levis': { lat: 46.80, lon: -71.18 },
  'trois-rivieres': { lat: 46.34, lon: -72.54 },
  'terrebonne': { lat: 45.70, lon: -73.65 },
  'brossard': { lat: 45.45, lon: -73.47 },
  'repentigny': { lat: 45.74, lon: -73.45 },
  'drummondville': { lat: 45.88, lon: -72.49 },
  'granby': { lat: 45.40, lon: -72.73 },

  // BC cities
  'vancouver': { lat: 49.28, lon: -123.12 },
  'surrey': { lat: 49.19, lon: -122.85 },
  'burnaby': { lat: 49.25, lon: -122.95 },
  'richmond': { lat: 49.17, lon: -123.14 },
  'victoria': { lat: 48.43, lon: -123.37 },
  'kelowna': { lat: 49.89, lon: -119.50 },
  'abbotsford': { lat: 49.05, lon: -122.33 },
  'coquitlam': { lat: 49.28, lon: -122.79 },

  // Alberta cities
  'calgary': { lat: 51.05, lon: -114.07 },
  'edmonton': { lat: 53.55, lon: -113.49 },
  'red deer': { lat: 52.27, lon: -113.81 },
  'lethbridge': { lat: 49.70, lon: -112.83 },

  // Other provinces
  'winnipeg': { lat: 49.90, lon: -97.14 },
  'saskatoon': { lat: 52.13, lon: -106.67 },
  'regina': { lat: 50.45, lon: -104.62 },
  'halifax': { lat: 44.65, lon: -63.58 },
  'saint john': { lat: 45.27, lon: -66.06 },
  'moncton': { lat: 46.09, lon: -64.78 },
  'fredericton': { lat: 45.96, lon: -66.64 },
  "st. john's": { lat: 47.56, lon: -52.71 },
  'charlottetown': { lat: 46.24, lon: -63.13 },

  // US cities
  'philadelphia': { lat: 39.95, lon: -75.17 },
  'pittsburgh': { lat: 40.44, lon: -80.00 },
  'new york': { lat: 40.71, lon: -74.01 },
  'brooklyn': { lat: 40.65, lon: -73.95 },
  'buffalo': { lat: 42.89, lon: -78.88 },
  'rochester': { lat: 43.16, lon: -77.61 },
  'boston': { lat: 42.36, lon: -71.06 },
  'baltimore': { lat: 39.29, lon: -76.61 },
  'washington': { lat: 38.91, lon: -77.04 },
  'columbus': { lat: 39.96, lon: -83.00 },
  'cleveland': { lat: 41.50, lon: -81.69 },
  'detroit': { lat: 42.33, lon: -83.05 },
  'chicago': { lat: 41.88, lon: -87.63 },
};

// Fallback province/state centroids (used when city not found)
const REGION_COORDS: Record<string, { lat: number; lon: number }> = {
  // Canadian Provinces
  'Ontario': { lat: 44.0, lon: -79.0 },
  'Quebec': { lat: 46.8, lon: -71.2 },
  'British Columbia': { lat: 49.3, lon: -123.1 },
  'Alberta': { lat: 51.0, lon: -114.1 },
  'Manitoba': { lat: 49.9, lon: -97.1 },
  'Saskatchewan': { lat: 52.1, lon: -106.7 },
  'Nova Scotia': { lat: 44.6, lon: -63.6 },
  'New Brunswick': { lat: 46.5, lon: -66.2 },
  'Newfoundland and Labrador': { lat: 47.6, lon: -52.7 },
  'Prince Edward Island': { lat: 46.2, lon: -63.1 },
  // US States (common ones)
  'Pennsylvania': { lat: 40.0, lon: -77.0 },
  'New York': { lat: 42.2, lon: -74.9 },
  'New Jersey': { lat: 40.1, lon: -74.7 },
  'Massachusetts': { lat: 42.4, lon: -71.4 },
  'Connecticut': { lat: 41.6, lon: -72.7 },
  'Maryland': { lat: 39.0, lon: -76.6 },
  'Virginia': { lat: 37.4, lon: -78.7 },
  'Ohio': { lat: 40.4, lon: -82.9 },
  'Michigan': { lat: 44.3, lon: -85.6 },
  'Florida': { lat: 27.8, lon: -81.8 },
  'California': { lat: 36.8, lon: -119.4 },
  'Texas': { lat: 31.0, lon: -100.0 },
  'Illinois': { lat: 40.6, lon: -89.4 },
  'Georgia': { lat: 32.2, lon: -83.5 },
  'North Carolina': { lat: 35.8, lon: -80.0 },
};

// Haversine formula for distance in km
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}


function titleCase(str: string): string {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Extract lastname for sorting (assumes "First Last" or "First Middle Last" format)
function getLastName(name: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  return parts.length > 0 ? parts[parts.length - 1].toLowerCase() : '';
}

// Get badge priority for sorting (higher = more important)
function getBadgePriority(shipment: Shipment): number {
  const orderCount = Number(shipment.customer?.order_count || 0);
  const tier = shipment.customer?.tier;

  // VIP = 6, HIGH = 5, MED = 4, 1st = 3, New = 2, standard = 1
  if (tier === 'vip') return 6;
  if (tier === 'high') return 5;
  if (tier === 'medium') return 4;
  if (orderCount === 1) return 3; // 1st order
  if (orderCount > 1 && orderCount <= 4 &&
      shipment.customer?.customer_since &&
      (Date.now() - new Date(shipment.customer.customer_since).getTime()) <= 90 * 24 * 60 * 60 * 1000) {
    return 2; // New customer
  }
  return 1; // standard
}

function formatDestination(shipment: Shipment): string {
  const { city, province } = shipment.shipping_address;
  const provAbbrev = REGION_ABBREV[province] || (province.length > 2 ? province.substring(0, 2).toUpperCase() : province);
  return `${titleCase(city)}, ${provAbbrev}`;
}

function parseLocation(location: string): { city: string; province: string } | null {
  if (!location) return null;
  // Parse formats like "Mississauga, ON" or "Toronto, Ontario"
  const parts = location.split(',').map(s => s.trim());
  if (parts.length >= 2) {
    return { city: parts[0], province: parts[1] };
  }
  // Try to match just a city name
  const normalizedCity = location.toLowerCase().trim();
  if (CITY_COORDS[normalizedCity]) {
    return { city: location, province: '' };
  }
  return null;
}

function getDistanceBetweenLocations(
  fromCity: string,
  fromProvince: string,
  toCity: string,
  toProvince: string
): number | null {
  // Get coordinates for origin
  const fromNormalized = fromCity.toLowerCase().trim();
  let fromCoords = CITY_COORDS[fromNormalized];
  if (!fromCoords && fromProvince) {
    fromCoords = REGION_COORDS[fromProvince];
  }
  if (!fromCoords) return null;

  // Get coordinates for destination
  const toNormalized = toCity.toLowerCase().trim();
  let toCoords = CITY_COORDS[toNormalized];
  if (!toCoords && toProvince) {
    toCoords = REGION_COORDS[toProvince];
  }
  if (!toCoords) return null;

  return calculateDistance(fromCoords.lat, fromCoords.lon, toCoords.lat, toCoords.lon);
}

function formatDistance(shipment: Shipment): string {
  // If we have a current location, calculate remaining distance to destination
  if (shipment.current_location) {
    const currentLoc = parseLocation(shipment.current_location);
    if (currentLoc) {
      const distance = getDistanceBetweenLocations(
        currentLoc.city,
        currentLoc.province,
        shipment.shipping_address.city,
        shipment.shipping_address.province
      );
      if (distance !== null) {
        return distance < 5 ? '<5 km' : `${distance} km`;
      }
    }
  }

  // No current location yet - show dash
  return '-';
}

const RISK_COLORS: Record<RiskLevel, string> = {
  low: 'green',
  medium: 'yellow',
  high: 'orange',
  critical: 'red',
};

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'LOW',
  medium: 'MEDIUM',
  high: 'HIGH',
  critical: 'WARNING',
};

const TIER_BADGES: Record<CustomerTier, { color: string; label: string }> = {
  vip: { color: 'grape', label: 'VIP' },
  high: { color: 'orange', label: 'HIGH' },
  medium: { color: 'blue', label: 'MED' },
  standard: { color: 'gray', label: '' },
};

const STATUS_LABELS: Record<ShipmentStatus, string> = {
  awaiting_fulfillment: 'Awaiting Ship',
  pending: 'Awaiting Ship',
  picked_up: 'Picked Up',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  exception: 'Exception',
};

const CARRIER_NAMES: Record<string, string> = {
  ecom: 'Ecom',
  loomis: 'Loomis',
  'gtagsm-loomis': 'GTAGSM (Loomis)',
  'gtagsm-e24': 'GTAGSM (E24)',
  gtagsm: 'GTAGSM',
  purolator: 'Purolator',
  ups: 'UPS',
};

/**
 * Get effective deadline by shifting estimated deadline when actual pickup
 * is later than expected ship date.
 */
function getEffectiveDeadline(shipment: Shipment): Date {
  if (!shipment.picked_up_at || !shipment.expected_ship_date || !shipment.deadline) {
    return shipment.deadline;
  }
  const expectedShip = shipment.expected_ship_date instanceof Date
    ? shipment.expected_ship_date
    : new Date(shipment.expected_ship_date);
  const pickedUp = shipment.picked_up_at instanceof Date
    ? shipment.picked_up_at
    : new Date(shipment.picked_up_at);
  const shiftMs = pickedUp.getTime() - expectedShip.getTime();

  // Only shift if pickup was later than expected (don't penalise early pickups)
  if (shiftMs <= 0) return shipment.deadline;

  return new Date(shipment.deadline.getTime() + shiftMs);
}

function formatDeadline(date: Date): string {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));

  if (diffHours < 0) {
    return `${Math.abs(diffHours)}h overdue`;
  }
  if (diffHours < 24) {
    return `${diffHours}h`;
  }
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays}d`;
}

function formatDeliveredDate(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatTimeInTransit(shipment: Shipment): string {
  // Need picked_up_at to calculate
  if (!shipment.picked_up_at) {
    return '-';
  }

  const start = new Date(shipment.picked_up_at);
  const end = shipment.delivered_at ? new Date(shipment.delivered_at) : new Date();
  const diffMs = end.getTime() - start.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));

  if (hours < 24) {
    return `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

function formatLastChecked(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// Sorting types
type SortColumn = 'tracking' | 'order' | 'customer' | 'customerTier' | 'destination' | 'distance' | 'carrier' | 'status' | 'location' | 'transit' | 'deadline' | 'risk' | 'shipped' | 'delivered' | 'lastChecked';
type SortDirection = 'asc' | 'desc' | 'none';

interface SortableHeaderProps {
  children: React.ReactNode;
  sorted: boolean;
  reversed: boolean;
  onSort: () => void;
  style?: React.CSSProperties;
}

function SortableHeader({ children, sorted, reversed, onSort, style }: SortableHeaderProps) {
  const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <Table.Th style={style}>
      <UnstyledButton onClick={onSort} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <Text fw={500} size="sm">{children}</Text>
        <Icon size={14} style={{ opacity: sorted ? 1 : 0.4 }} />
      </UnstyledButton>
    </Table.Th>
  );
}

// Sort comparison functions
function compareValues<T>(a: T, b: T, direction: SortDirection): number {
  if (direction === 'none') return 0;
  if (a === b) return 0;
  if (a === null || a === undefined) return direction === 'asc' ? 1 : -1;
  if (b === null || b === undefined) return direction === 'asc' ? -1 : 1;
  const comparison = a < b ? -1 : 1;
  return direction === 'asc' ? comparison : -comparison;
}

function sortShipments(shipments: Shipment[], column: SortColumn, direction: SortDirection): Shipment[] {
  // If direction is 'none', return original order
  if (direction === 'none') {
    return [...shipments];
  }

  return [...shipments].sort((a, b) => {
    switch (column) {
      case 'tracking':
        return compareValues(a.tracking_number || '', b.tracking_number || '', direction);
      case 'order':
        return compareValues(a.shopify_order_name, b.shopify_order_name, direction);
      case 'customer':
        // Sort by lastname, then firstname for ties
        const lastA = getLastName(a.customer?.name || '');
        const lastB = getLastName(b.customer?.name || '');
        if (lastA !== lastB) {
          return compareValues(lastA, lastB, direction);
        }
        return compareValues(a.customer?.name?.toLowerCase(), b.customer?.name?.toLowerCase(), direction);
      case 'customerTier':
        // Sort by badge priority (VIP > HIGH > MED > 1st > New > standard)
        return compareValues(getBadgePriority(a), getBadgePriority(b), direction);
      case 'destination':
        return compareValues(
          `${a.shipping_address?.city}, ${a.shipping_address?.province}`.toLowerCase(),
          `${b.shipping_address?.city}, ${b.shipping_address?.province}`.toLowerCase(),
          direction
        );
      case 'distance': {
        // Parse distance values for comparison
        const parseDistance = (s: Shipment): number => {
          if (!s.current_location) return Infinity; // No location = at end
          const loc = parseLocation(s.current_location);
          if (!loc) return Infinity;
          const dist = getDistanceBetweenLocations(
            loc.city, loc.province,
            s.shipping_address.city, s.shipping_address.province
          );
          return dist ?? Infinity;
        };
        return compareValues(parseDistance(a), parseDistance(b), direction);
      }
      case 'carrier':
        return compareValues(a.carrier_code, b.carrier_code, direction);
      case 'status':
        return compareValues(a.status, b.status, direction);
      case 'location':
        return compareValues(a.current_location, b.current_location, direction);
      case 'deadline':
        return compareValues(
          getEffectiveDeadline(a).getTime(),
          getEffectiveDeadline(b).getTime(),
          direction
        );
      case 'risk': {
        const riskOrder: Record<RiskLevel, number> = { critical: 4, high: 3, medium: 2, low: 1 };
        return compareValues(riskOrder[a.risk_level], riskOrder[b.risk_level], direction);
      }
      case 'transit': {
        const transitA = a.picked_up_at ? new Date(a.picked_up_at).getTime() : 0;
        const transitB = b.picked_up_at ? new Date(b.picked_up_at).getTime() : 0;
        return compareValues(transitA, transitB, direction);
      }
      case 'shipped':
        return compareValues(
          a.picked_up_at ? new Date(a.picked_up_at).getTime() : 0,
          b.picked_up_at ? new Date(b.picked_up_at).getTime() : 0,
          direction
        );
      case 'delivered':
        return compareValues(
          a.delivered_at ? new Date(a.delivered_at).getTime() : 0,
          b.delivered_at ? new Date(b.delivered_at).getTime() : 0,
          direction
        );
      case 'lastChecked':
        return compareValues(
          a.tracking_refreshed_at ? new Date(a.tracking_refreshed_at).getTime() : 0,
          b.tracking_refreshed_at ? new Date(b.tracking_refreshed_at).getTime() : 0,
          direction
        );
      default:
        return 0;
    }
  });
}

function LoadingRows() {
  return (
    <>
      {[1, 2, 3, 4, 5].map((i) => (
        <Table.Tr key={i}>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
          <Table.Td><Skeleton height={20} /></Table.Td>
        </Table.Tr>
      ))}
    </>
  );
}

export function ShipmentTable({
  shipments,
  loading,
  onSelect,
  selectedId,
  filter,
  viewMode = 'inflight',
}: ShipmentTableProps) {
  const isDeliveredView = filter === 'delivered_today' || filter === 'delivered_yesterday' || viewMode === 'complete';

  // Calculate container height: viewport minus header and filter bar
  // Inflight has StatsHeader (~280px total above), Complete doesn't (~180px)
  const containerHeight = viewMode === 'inflight' ? 'calc(100vh - 280px)' : 'calc(100vh - 180px)';

  // Sorting state - default to 'none' for natural order (deadline for inflight, delivered_at for complete)
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('none');

  // Tri-state sorting: asc → desc → none (reset)
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Cycle through: asc → desc → none
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        // Reset to natural order
        setSortColumn(null);
        setSortDirection('none');
      } else {
        setSortDirection('asc');
      }
    } else {
      // New column: start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Apply sorting - when sortColumn is null, return original order (natural order from query)
  const sortedShipments = useMemo(() => {
    if (!sortColumn || sortDirection === 'none') {
      return shipments;
    }
    return sortShipments(shipments, sortColumn, sortDirection);
  }, [shipments, sortColumn, sortDirection]);

  return (
    <Box style={{ overflowY: 'auto', overflowX: 'auto', width: '100%', height: containerHeight }}>
      <Table striped highlightOnHover style={{ width: '100%' }} stickyHeader stickyHeaderOffset={0}>
        <Table.Thead style={{ backgroundColor: 'var(--mantine-color-body)' }}>
          <Table.Tr>
            <SortableHeader
              sorted={sortColumn === 'tracking'}
              reversed={sortDirection === 'desc'}
              onSort={() => handleSort('tracking')}
            >
              Tracking
            </SortableHeader>
            <SortableHeader
              sorted={sortColumn === 'order'}
              reversed={sortDirection === 'desc'}
              onSort={() => handleSort('order')}
            >
              Order
            </SortableHeader>
            <SortableHeader
              sorted={sortColumn === 'customerTier'}
              reversed={sortDirection === 'desc'}
              onSort={() => handleSort('customerTier')}
              style={{ width: 50, padding: '8px 4px' }}
            >
              {' '}
            </SortableHeader>
            <SortableHeader
              sorted={sortColumn === 'customer'}
              reversed={sortDirection === 'desc'}
              onSort={() => handleSort('customer')}
            >
              Customer
            </SortableHeader>
            <SortableHeader
              sorted={sortColumn === 'destination'}
              reversed={sortDirection === 'desc'}
              onSort={() => handleSort('destination')}
            >
              Destination
            </SortableHeader>
            {!isDeliveredView && (
              <SortableHeader
                sorted={sortColumn === 'distance'}
                reversed={sortDirection === 'desc'}
                onSort={() => handleSort('distance')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Distance
              </SortableHeader>
            )}
            <SortableHeader
              sorted={sortColumn === 'carrier'}
              reversed={sortDirection === 'desc'}
              onSort={() => handleSort('carrier')}
            >
              Carrier
            </SortableHeader>
            <SortableHeader
              sorted={sortColumn === 'status'}
              reversed={sortDirection === 'desc'}
              onSort={() => handleSort('status')}
            >
              Status
            </SortableHeader>
            {!isDeliveredView && (
              <SortableHeader
                sorted={sortColumn === 'location'}
                reversed={sortDirection === 'desc'}
                onSort={() => handleSort('location')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Location
              </SortableHeader>
            )}
            <SortableHeader
              sorted={sortColumn === 'transit'}
              reversed={sortDirection === 'desc'}
              onSort={() => handleSort('transit')}
              style={{ whiteSpace: 'nowrap' }}
            >
              Transit
            </SortableHeader>
            {!isDeliveredView && (
              <SortableHeader
                sorted={sortColumn === 'lastChecked'}
                reversed={sortDirection === 'desc'}
                onSort={() => handleSort('lastChecked')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Last Checked
              </SortableHeader>
            )}
            {isDeliveredView && (
              <SortableHeader
                sorted={sortColumn === 'shipped'}
                reversed={sortDirection === 'desc'}
                onSort={() => handleSort('shipped')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Shipped
              </SortableHeader>
            )}
            <SortableHeader
              sorted={sortColumn === (isDeliveredView ? 'delivered' : 'deadline')}
              reversed={sortDirection === 'desc'}
              onSort={() => handleSort(isDeliveredView ? 'delivered' : 'deadline')}
              style={{ whiteSpace: 'nowrap' }}
            >
              {isDeliveredView ? 'Delivered' : 'Deadline'}
            </SortableHeader>
            {!isDeliveredView && (
              <SortableHeader
                sorted={sortColumn === 'risk'}
                reversed={sortDirection === 'desc'}
                onSort={() => handleSort('risk')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Risk
              </SortableHeader>
            )}
            {!isDeliveredView && (
              <Table.Th style={{ width: 40, textAlign: 'center' }}>Act</Table.Th>
            )}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading ? (
            <LoadingRows />
          ) : sortedShipments.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={isDeliveredView ? 10 : 14}>
                <Text ta="center" c="dimmed" py="xl">
                  {isDeliveredView ? 'No delivered shipments' : 'No active shipments'}
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            sortedShipments.map((shipment) => {
              const tierBadge = TIER_BADGES[shipment.customer.tier];
              const effectiveDeadline = getEffectiveDeadline(shipment);
              const deadlineStr = formatDeadline(effectiveDeadline);
              const isOverdue = effectiveDeadline < new Date();
              const isSelected = shipment.id === selectedId;

              return (
                <Table.Tr
                  key={shipment.id}
                  onClick={() => onSelect(shipment)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: isSelected ? 'var(--mantine-color-blue-1)' : undefined,
                  }}
                >
                  <Table.Td>
                    <Text size="sm" style={{ fontFamily: 'monospace' }}>
                      {shipment.tracking_number || '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text c="dimmed">{shipment.shopify_order_name}</Text>
                  </Table.Td>
                  <Table.Td style={{ padding: '8px 4px', width: 50 }}>
                    {(() => {
                      const orderCount = Number(shipment.customer.order_count);
                      const isFirstOrder = orderCount === 1;
                      const isNewCustomer = orderCount > 1 && orderCount <= 4 &&
                        shipment.customer.customer_since &&
                        (Date.now() - new Date(shipment.customer.customer_since).getTime()) <= 90 * 24 * 60 * 60 * 1000;

                      let badgeLabel = tierBadge.label;
                      let badgeColor = tierBadge.color;

                      if (isFirstOrder) {
                        badgeLabel = '1st';
                        badgeColor = 'teal';
                      } else if (isNewCustomer) {
                        badgeLabel = 'New';
                        badgeColor = 'cyan';
                      }

                      return badgeLabel ? (
                        <Badge size="xs" color={badgeColor} style={{ minWidth: 36 }}>
                          {badgeLabel}
                        </Badge>
                      ) : null;
                    })()}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm">{titleCase(shipment.customer.name)}</Text>
                      {!shipment.customer.is_subscriber &&
                        (shipment.customer.order_count >= 2 || shipment.customer.lifetime_spend >= 200) && (
                        <Badge size="xs" color="violet" variant="filled" style={{ padding: '0 4px' }} title="Conversion Target">
                          CT
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      {shipment.market === 'usa' && <Text size="sm">🇺🇸</Text>}
                      <Text size="sm">{formatDestination(shipment)}</Text>
                    </Group>
                  </Table.Td>
                  {!isDeliveredView && (
                    <Table.Td>
                      <Text size="sm">{formatDistance(shipment)}</Text>
                    </Table.Td>
                  )}
                  <Table.Td>
                    <Text size="sm">
                      {shipment.carrier_code
                        ? CARRIER_NAMES[shipment.carrier_code] ?? shipment.carrier_code
                        : '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Text size="sm" c={isDeliveredView ? 'green' : undefined}>
                        {isDeliveredView
                          ? 'Delivered'
                          : shipment.events?.length > 0
                            ? shipment.events[0].description
                            : STATUS_LABELS[shipment.status]}
                      </Text>
                      {shipment.status === 'exception' && shipment.exception_subtype && (
                        <Badge size="xs" color="red" variant="light">
                          {shipment.exception_subtype}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  {!isDeliveredView && (
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Text size="sm" c="dimmed">
                        {(() => {
                          if (shipment.current_location) {
                            // Format Loomis ETOBICOKE as "Loomis ETOB DC"
                            if (shipment.current_location.toUpperCase() === 'ETOBICOKE' &&
                                (shipment.carrier_code === 'loomis' || shipment.carrier_code === 'gtagsm-loomis')) {
                              return 'Loomis ETOB DC';
                            }
                            return shipment.current_location;
                          }
                          if (shipment.status === 'awaiting_fulfillment' || shipment.status === 'pending') {
                            return shipment.market === 'usa' ? 'Revive PENN DC' : 'Revive ETOB DC';
                          }
                          return '-';
                        })()}
                      </Text>
                    </Table.Td>
                  )}
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    <Text size="sm">
                      {formatTimeInTransit(shipment)}
                    </Text>
                  </Table.Td>
                  {!isDeliveredView && (
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Text size="sm" c="dimmed">
                        {formatLastChecked(shipment.tracking_refreshed_at)}
                      </Text>
                    </Table.Td>
                  )}
                  {isDeliveredView && (
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Text size="sm">
                        {formatDeliveredDate(shipment.picked_up_at)}
                      </Text>
                    </Table.Td>
                  )}
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    <Text size="sm" c={!isDeliveredView && isOverdue ? 'red' : undefined}>
                      {isDeliveredView ? formatDeliveredDate(shipment.delivered_at) : deadlineStr}
                    </Text>
                  </Table.Td>
                  {!isDeliveredView && (
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Badge color={RISK_COLORS[shipment.risk_level]} size="sm">
                        {RISK_LABELS[shipment.risk_level]}
                      </Badge>
                    </Table.Td>
                  )}
                  {!isDeliveredView && (
                    <Table.Td style={{ textAlign: 'center' }}>
                      {shipment.recommended_actions?.some(a => a.priority === 'high') && (
                        <Badge size="sm" circle color="violet" variant="filled">
                          {shipment.recommended_actions.filter(a => a.priority === 'high').length}
                        </Badge>
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })
          )}
        </Table.Tbody>
      </Table>
    </Box>
  );
}
